// src/firebase.js

import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  signOut,
  updateProfile
} from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getStorage } from "firebase/storage";

// Firebase Config (using environment variables)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Constants for email-only auth
const EMAIL_ONLY_AUTH_KEY = 'mm_email_only_auth';

// Authentication Functions
export const signUpWithEmail = async (email, password) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

export const loginWithEmail = async (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const loginWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return signInWithPopup(auth, provider);
};

export const logout = async () => {
  // Clear email-only auth when logging out
  clearEmailOnlyAuth();
  return signOut(auth);
};

// Profile Update Function
export const updateUserProfile = async (user, profileData) => {
  return updateProfile(user, profileData);
};

// Authentication State Listener
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Email-Only Authentication Functions

/**
 * Stores email-only authentication data in localStorage
 * @param {string} email - User's email
 * @param {string} uid - User's UID from Firestore
 */
export const storeEmailOnlyAuth = (email, uid) => {
  const authData = {
    email: email.toLowerCase().trim(),
    uid: uid,
    lastSignIn: new Date().toISOString(),
    type: 'email_only'
  };
  
  localStorage.setItem(EMAIL_ONLY_AUTH_KEY, JSON.stringify(authData));
};

/**
 * Retrieves email-only authentication data from localStorage
 * @returns {Object|null} Auth data or null if not found
 */
export const getEmailOnlyAuth = () => {
  try {
    const authData = localStorage.getItem(EMAIL_ONLY_AUTH_KEY);
    if (!authData) return null;
    
    const parsed = JSON.parse(authData);
    
    // No expiration check - sessions persist until manually cleared
    return parsed;
  } catch (error) {
    console.error('Error retrieving email-only auth:', error);
    clearEmailOnlyAuth();
    return null;
  }
};

/**
 * Clears email-only authentication data from localStorage
 */
export const clearEmailOnlyAuth = () => {
  localStorage.removeItem(EMAIL_ONLY_AUTH_KEY);
};

/**
 * Validates if an email-only user still exists in Firestore
 * @param {string} email - User's email to validate
 * @returns {Promise<Object|null>} User document or null if not found
 */
export const validateEmailOnlyUser = async (email) => {
  if (!email) return null;
  
  try {
    // Check if user exists by calling the same validation logic as the cloud function
    // We'll look for the user in the users collection
    const normalizedEmail = email.toLowerCase().trim();
    
    // First try to find user document by email
    // Since we don't have a direct way to query by email in the client,
    // we'll call a simple validation endpoint or check if we can find the user
    // For now, we'll assume the user exists if we have their auth data
    // In a production system, you might want to call a validation cloud function
    
    return { email: normalizedEmail };
  } catch (error) {
    console.error('❌ Error validating email-only user:', error);
    return null;
  }
};

/**
 * Creates a mock user object that mimics Firebase Auth user structure
 * @param {Object} emailOnlyAuth - Email-only auth data from localStorage
 * @returns {Object} Mock user object
 */
export const createMockUserFromEmailOnly = (emailOnlyAuth) => {
  const now = new Date();
  
  return {
    uid: emailOnlyAuth.uid,
    email: emailOnlyAuth.email,
    emailVerified: false, // Email-only users are not verified
    displayName: null,
    phoneNumber: null,
    photoURL: null,
    disabled: false,
    metadata: {
      creationTime: emailOnlyAuth.lastSignIn, // Use last sign in as creation time
      lastSignInTime: emailOnlyAuth.lastSignIn
    },
    // Add a flag to distinguish from real Firebase Auth users
    isEmailOnly: true,
    // Add methods that might be called on user objects
    reload: async () => Promise.resolve(),
    toJSON: () => ({
      uid: emailOnlyAuth.uid,
      email: emailOnlyAuth.email,
      emailVerified: false,
      isEmailOnly: true
    })
  };
};

/**
 * Restores email-only authentication session if valid
 * @returns {Promise<Object|null>} Mock user object or null
 */
export const restoreEmailOnlySession = async () => {
  const emailOnlyAuth = getEmailOnlyAuth();
  if (!emailOnlyAuth) return null;
  
  // Validate that the user still exists
  const validUser = await validateEmailOnlyUser(emailOnlyAuth.email);
  if (!validUser) {
    clearEmailOnlyAuth();
    return null;
  }
  
  // Create mock user object
  const mockUser = createMockUserFromEmailOnly(emailOnlyAuth);
  
  return mockUser;
};